import { smoothScrollBehavior } from "./motion";
import type { QuoteAnchor, QuoteMatch, QuoteSegment, ReviewComment } from "./types";

const REVIEW_BLOCK_SELECTOR = "[data-review-block]";

function overlapFromEnd(actual: string, expected: string): number {
  const max = Math.min(actual.length, expected.length);
  for (let length = max; length > 0; length -= 1) {
    if (actual.slice(-length) === expected.slice(-length)) return length;
  }
  return 0;
}

function overlapFromStart(actual: string, expected: string): number {
  const max = Math.min(actual.length, expected.length);
  for (let length = max; length > 0; length -= 1) {
    if (actual.slice(0, length) === expected.slice(0, length)) return length;
  }
  return 0;
}

export function createQuoteAnchor(
  blockText: string,
  start: number,
  end: number,
): QuoteAnchor | null {
  const safeStart = Math.max(0, Math.min(start, blockText.length));
  const safeEnd = Math.max(safeStart, Math.min(end, blockText.length));
  const text = blockText.slice(safeStart, safeEnd).trim();
  if (!text || text.length > 500) return null;

  const actualStart = blockText.indexOf(text, safeStart);
  const anchorStart = actualStart >= 0 ? actualStart : safeStart;
  const anchorEnd = anchorStart + text.length;
  return {
    text,
    prefix: blockText.slice(Math.max(0, anchorStart - 120), anchorStart),
    suffix: blockText.slice(anchorEnd, Math.min(blockText.length, anchorEnd + 120)),
  };
}

interface SourceCharacter {
  blockIndex: number;
  start: number;
  end: number;
}

interface DocumentTextIndex {
  text: string;
  sources: Array<SourceCharacter | null>;
}

function normalizedText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function buildDocumentIndex(blocks: string[]): DocumentTextIndex {
  const output: string[] = [];
  const sources: Array<SourceCharacter | null> = [];
  const appendSpace = (source: SourceCharacter | null) => {
    if (!output.length || output[output.length - 1] === " ") {
      const previous = sources[sources.length - 1];
      if (previous && source && previous.blockIndex === source.blockIndex) previous.end = source.end;
      return;
    }
    output.push(" ");
    sources.push(source);
  };

  blocks.forEach((block, blockIndex) => {
    if (blockIndex > 0) appendSpace(null);
    for (let offset = 0; offset < block.length; offset += 1) {
      const character = block[offset] || "";
      const source = { blockIndex, start: offset, end: offset + 1 };
      if (/\s/.test(character)) appendSpace(source);
      else {
        output.push(character);
        sources.push(source);
      }
    }
  });

  while (output[0] === " ") {
    output.shift();
    sources.shift();
  }
  while (output[output.length - 1] === " ") {
    output.pop();
    sources.pop();
  }
  return { text: output.join(""), sources };
}

function segmentsForMatch(
  sources: Array<SourceCharacter | null>,
  start: number,
  end: number,
): QuoteSegment[] {
  const segments: QuoteSegment[] = [];
  sources.slice(start, end).forEach((source) => {
    if (!source) return;
    const current = segments[segments.length - 1];
    if (current && current.blockIndex === source.blockIndex && source.start <= current.end) {
      current.end = Math.max(current.end, source.end);
    } else {
      segments.push({ blockIndex: source.blockIndex, start: source.start, end: source.end });
    }
  });
  return segments;
}

export function resolveQuoteMatch(blocks: string[], anchor: QuoteAnchor): QuoteMatch | null {
  if (!anchor.text) return null;
  const candidates: QuoteMatch[] = [];
  const documentIndex = buildDocumentIndex(blocks);
  const quoteText = normalizedText(anchor.text);
  const quotePrefix = normalizedText(anchor.prefix);
  const quoteSuffix = normalizedText(anchor.suffix);
  if (!quoteText) return null;

  let start = documentIndex.text.indexOf(quoteText);
  while (start >= 0) {
    const end = start + quoteText.length;
    const before = documentIndex.text.slice(Math.max(0, start - 120), start);
    const after = documentIndex.text.slice(end, Math.min(documentIndex.text.length, end + 120));
    const prefixScore = quotePrefix ? overlapFromEnd(before, quotePrefix) : 0;
    const suffixScore = quoteSuffix ? overlapFromStart(after, quoteSuffix) : 0;
    const fullPrefixBonus = quotePrefix && before.endsWith(quotePrefix) ? 200 : 0;
    const fullSuffixBonus = quoteSuffix && after.startsWith(quoteSuffix) ? 200 : 0;
    const segments = segmentsForMatch(documentIndex.sources, start, end);
    if (segments.length) {
      candidates.push({
        segments,
        score: prefixScore + suffixScore + fullPrefixBonus + fullSuffixBonus,
      });
    }
    start = documentIndex.text.indexOf(quoteText, start + 1);
  }

  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0] || null;

  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0];
  const next = candidates[1];
  if (!best || (next && best.score === next.score)) return null;
  return best;
}

function closestReviewBlock(node: Node): HTMLElement | null {
  const element = node.nodeType === Node.ELEMENT_NODE
    ? (node as Element)
    : node.parentElement;
  return element?.closest<HTMLElement>(REVIEW_BLOCK_SELECTOR) || null;
}

export function captureSelection(reader: HTMLElement): QuoteAnchor | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  const startBlock = closestReviewBlock(range.startContainer);
  const endBlock = closestReviewBlock(range.endContainer);
  if (!startBlock || !endBlock || !reader.contains(startBlock) || !reader.contains(endBlock)) return null;

  const before = document.createRange();
  before.selectNodeContents(reader);
  before.setEnd(range.startContainer, range.startOffset);
  const after = document.createRange();
  after.selectNodeContents(reader);
  after.setStart(range.endContainer, range.endOffset);

  const text = range.toString().trim();
  if (!text || text.length > 500) return null;
  return {
    text,
    prefix: before.toString().slice(-120),
    suffix: after.toString().slice(0, 120),
  };
}

export function anchorForBlock(block: HTMLElement): QuoteAnchor | null {
  const text = (block.textContent || "").trim();
  if (!text) return null;
  const quote = text.slice(0, 500);
  return { text: quote, prefix: "", suffix: text.slice(quote.length, quote.length + 120) };
}

function domPointForOffset(root: HTMLElement, offset: number): { node: Text; offset: number } | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let consumed = 0;
  let current = walker.nextNode();
  while (current) {
    const textNode = current as Text;
    const length = textNode.data.length;
    if (offset <= consumed + length) {
      return { node: textNode, offset: Math.max(0, offset - consumed) };
    }
    consumed += length;
    current = walker.nextNode();
  }
  return null;
}

export function clearQuoteHighlight(reader: HTMLElement): void {
  reader.querySelectorAll("mark.quote-highlight").forEach((mark) => {
    const parent = mark.parentNode;
    while (mark.firstChild) parent?.insertBefore(mark.firstChild, mark);
    mark.remove();
    parent?.normalize();
  });
  reader.querySelectorAll(".quote-focus").forEach((block) => block.classList.remove("quote-focus"));
}

export function updateReviewBlockHighlights(
  reader: HTMLElement,
  comments: ReviewComment[],
): void {
  const blocks = Array.from(reader.querySelectorAll<HTMLElement>(REVIEW_BLOCK_SELECTOR));
  blocks.forEach((block) => block.classList.remove("review-has-open", "review-has-done"));
  const blockTexts = blocks.map((block) => block.textContent || "");
  const states = new Map<number, "open" | "done">();

  comments.forEach((comment) => {
    if (!comment.quote_text) return;
    const match = resolveQuoteMatch(blockTexts, {
      text: comment.quote_text,
      prefix: comment.quote_prefix || "",
      suffix: comment.quote_suffix || "",
    });
    match?.segments.forEach(({ blockIndex }) => {
      const current = states.get(blockIndex);
      if (comment.status === "open" || !current) states.set(blockIndex, comment.status);
    });
  });

  states.forEach((status, blockIndex) => {
    blocks[blockIndex]?.classList.add(status === "open" ? "review-has-open" : "review-has-done");
  });
}

export function focusQuote(reader: HTMLElement, anchor: QuoteAnchor): boolean {
  clearQuoteHighlight(reader);
  const blocks = Array.from(reader.querySelectorAll<HTMLElement>(REVIEW_BLOCK_SELECTOR));
  const match = resolveQuoteMatch(blocks.map((block) => block.textContent || ""), anchor);
  if (!match) return false;

  match.segments.forEach((segment, index) => {
    const block = blocks[segment.blockIndex];
    if (!block) return;
    block.classList.add("quote-focus");
    if (index === 0) block.scrollIntoView({ behavior: smoothScrollBehavior(), block: "center" });

    const start = domPointForOffset(block, segment.start);
    const end = domPointForOffset(block, segment.end);
    if (start && end) {
      try {
        const range = document.createRange();
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset);
        const mark = document.createElement("mark");
        mark.className = "quote-highlight";
        mark.appendChild(range.extractContents());
        range.insertNode(mark);
      } catch {
        // The block-level focus remains a safe fallback for complex inline markup.
      }
    }
  });

  window.setTimeout(() => clearQuoteHighlight(reader), 3200);
  return true;
}
